/**
 * Confidence score utilities for consistent styling across CMS sync components.
 *
 * Consolidates the confidence color logic that was previously duplicated in:
 * - sync-suggestions/ConfidenceScore.tsx
 * - sync-suggestions/SyncSuggestionBadge.tsx
 * - unified-view/SuggestedLinkBadge.tsx
 */

/**
 * Confidence thresholds used across the application.
 * - HIGH (>= 0.9): High confidence, shown in emerald/green
 * - MEDIUM (>= 0.7): Medium confidence, shown in amber/yellow
 * - LOW (< 0.7): Needs review, shown in red
 */
export const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.9,
  MEDIUM: 0.7,
} as const;

/**
 * Full confidence color result with all styling variants
 */
export interface ConfidenceColorResult {
  /** Stroke color for SVG elements (e.g., 'stroke-emerald-500') */
  stroke: string;
  /** Text color (e.g., 'text-emerald-600') */
  text: string;
  /** Human-readable label (e.g., 'High confidence') */
  label: string;
  /** Background color (e.g., 'bg-emerald-50') */
  bg: string;
  /** Solid background for dots/indicators (e.g., 'bg-emerald-500') */
  solid: string;
  /** Border color (e.g., 'border-emerald-200') */
  border: string;
}

/**
 * Get comprehensive confidence styling based on score.
 *
 * @param confidence - Confidence score between 0 and 1
 * @returns Object with all color variants for consistent styling
 *
 * @example
 * const colors = getConfidenceColors(0.95);
 * // colors.text -> 'text-emerald-600'
 * // colors.bg -> 'bg-emerald-50'
 * // colors.label -> 'High confidence'
 */
export function getConfidenceColors(confidence: number): ConfidenceColorResult {
  // Normalize percentage values (0-100) to decimal (0-1)
  const normalized = confidence > 1 ? confidence / 100 : confidence;

  if (normalized >= CONFIDENCE_THRESHOLDS.HIGH) {
    return {
      stroke: 'stroke-emerald-500',
      text: 'text-emerald-600',
      label: 'High confidence',
      bg: 'bg-emerald-50',
      solid: 'bg-emerald-500',
      border: 'border-emerald-200',
    };
  }
  if (normalized >= CONFIDENCE_THRESHOLDS.MEDIUM) {
    return {
      stroke: 'stroke-amber-500',
      text: 'text-amber-600',
      label: 'Medium confidence',
      bg: 'bg-amber-50',
      solid: 'bg-amber-500',
      border: 'border-amber-200',
    };
  }
  return {
    stroke: 'stroke-red-500',
    text: 'text-red-600',
    label: 'Needs review',
    bg: 'bg-red-50',
    solid: 'bg-red-500',
    border: 'border-rose-200',
  };
}

/**
 * Get just the text color class for confidence display.
 * Convenience function for simple text coloring.
 *
 * @param confidence - Confidence score between 0 and 1
 * @returns Tailwind text color class
 */
export function getConfidenceTextColor(confidence: number): string {
  return getConfidenceColors(confidence).text;
}

/**
 * Get just the solid background color class for confidence indicators.
 * Used for small dots and badges.
 *
 * @param confidence - Confidence score between 0 and 1
 * @returns Tailwind background color class
 */
export function getConfidenceBgColor(confidence: number): string {
  return getConfidenceColors(confidence).solid;
}

/**
 * Normalize a confidence value to a percentage (0-100).
 * Accepts either decimal (0-1) or percentage (0-100) input.
 *
 * @param confidence - Raw confidence value, may be null/undefined
 * @returns Confidence as a percentage (0-100), defaults to 0
 */
export function normalizeConfidence(
  confidence: number | null | undefined,
): number {
  const raw = confidence ?? 0;
  return raw <= 1 ? raw * 100 : raw;
}
